import _ from 'underscore';
import { store } from '../../store/store';
import { FinishedState, ModuleStateDB } from '../../data/datatypes';
import { addDurationToDate, parseISODuration } from '../DateTimeService/DateTimeService';
import { applyVisibilityRule, ElementValueBasedVisibilityCondition, getResolvedModuleStartDate, isVisibleForModuleModifiers, isVisibleForUserProperties, ModuleDataDB, SectionIdWithModifiers, SessionData, sessionIsVisibleForUserWithModifiers, SlotData, slotIsVisibleForUser, StateProperty, StateVisibilityRule } from '@app/common';
import { getOrderedContentIds } from '../../utils/contentOrder';

export const isVisibleForCurrSessionState = (rule: StateVisibilityRule): boolean => {
    const transientStateProperties = store.getState().moduleState.transientStateProperties;
    return applyVisibilityRule(rule, transientStateProperties);
};

export const isVisibleForTimeBasedRules = (
    module: ModuleDataDB,
    modules: ModuleDataDB[],
    userStartDateISO: string | undefined,
    now: Date = new Date()
): boolean => {
    if (!userStartDateISO) {
        return true;
    }

    const visibility = module.userSurveyStartBasedVisibility;
    if (!visibility?.duration) {
        return true;
    }

    const moduleStart = getResolvedModuleStartDate(module, modules, userStartDateISO);
    if (!moduleStart) {
        return false;
    }

    const duration = parseISODuration(visibility.duration);
    const moduleEnd = addDurationToDate(moduleStart, duration);

    return now >= moduleStart && now <= moduleEnd;
};

function getElementValue(
    elementValues: Record<string, string | number | boolean>,
    sectionId: string,
    itemId: string,
    elementId: string
): string | number | boolean | null {
    const key = `${sectionId}__${itemId}__${elementId}`;
    return elementValues[key] ?? null;
}

function evaluateVisibilityCondition(
    condition: ElementValueBasedVisibilityCondition,
    elementValues: Record<string, string | number | boolean>,
): boolean {
    const actualValue = getElementValue(
        elementValues,
        condition.sourceSectionId,
        condition.sourceItemId,
        condition.sourceElementId,
    );

    if (actualValue === null) {
        // Could not find the element value; assume false
        return false;
    }

    const conditionValue = condition.value;

    const normalizeArrayValue = (value: unknown): unknown[] => {
        if (!Array.isArray(value)) {
            return [];
        }
        return value.map(entry => {
            if (entry && typeof entry === 'object' && 'value' in (entry as any)) {
                return (entry as any).value;
            }
            return entry;
        });
    };

    switch (condition.operator) {
        case 'equals':
            return actualValue === conditionValue;
        case 'not_equals':
            return actualValue !== conditionValue;
        case 'includes':
            if (Array.isArray(actualValue)) {
                const normalizedActual = normalizeArrayValue(actualValue);
                if (Array.isArray(conditionValue)) {
                    return conditionValue.some(v => normalizedActual.includes(v));
                }
                return normalizedActual.includes(conditionValue as any);
            }
            if (typeof actualValue === 'string') {
                if (Array.isArray(conditionValue)) {
                    return conditionValue.some(v => actualValue.includes(String(v)));
                }
                return actualValue.includes(String(conditionValue));
            }
            return false;
        case 'excludes':
            if (Array.isArray(actualValue)) {
                const normalizedActual = normalizeArrayValue(actualValue);
                if (Array.isArray(conditionValue)) {
                    return conditionValue.every(v => !normalizedActual.includes(v));
                }
                return !normalizedActual.includes(conditionValue as any);
            }
            if (typeof actualValue === 'string') {
                if (Array.isArray(conditionValue)) {
                    return conditionValue.every(v => !actualValue.includes(String(v)));
                }
                return !actualValue.includes(String(conditionValue));
            }
            return false;
        case 'greater_than':
            return typeof actualValue === 'number' && actualValue > Number(conditionValue);
        case 'greater_or_equals':
            return typeof actualValue === 'number' && actualValue >= Number(conditionValue);
        case 'less_than':
            return typeof actualValue === 'number' && actualValue < Number(conditionValue);
        case 'less_or_equals':
            return typeof actualValue === 'number' && actualValue <= Number(conditionValue);
        default:
            // Unknown operator
            return false;
    }
}

function isVisibleForElementValueConditions(
    visibilityConditions: ElementValueBasedVisibilityCondition[] | undefined,
    elementValues: Record<string, string | number | boolean>,
): boolean {
    if (!visibilityConditions || visibilityConditions.length === 0 || !elementValues) {
        // No conditions means always visible
        return true;
    }

    // Evaluate all conditions (AND logic)
    return visibilityConditions.every(condition =>
        evaluateVisibilityCondition(condition, elementValues)
    );
}

export const getVisibleSections = (
    session: SessionData,
    moduleModifiers: string[],
    elementValues: Record<string, string | number | boolean>,
    userProperties: string[],
    transientStateProperties?: StateProperty[]
): SectionIdWithModifiers[] => {
    // filter with userproperties
    const visibleForUser = session.sections.filter(section => isVisibleForUserProperties(section.generalDisplayRule, userProperties));

    // filter with modulemodifiers
    const visibleForModuleModifiers = visibleForUser.filter(section => {
        if (!section.moduleDisplayRule) {
            return true;
        }
        return isVisibleForModuleModifiers(section.moduleDisplayRule, moduleModifiers);
    });

    // filter with stateproperties
    const activeTransientStateProperties = transientStateProperties ?? store.getState().moduleState.transientStateProperties;
    const visibleForCurrSession = visibleForModuleModifiers.filter(section =>  {
        if (!section.recommendationRule) {
            return true;
        }
        return applyVisibilityRule(section.recommendationRule, activeTransientStateProperties);
    });

    // filter with element value conditions
    const visible = visibleForCurrSession.filter(section => {
        if (!section.elementValueBasedVisibilityConditions) {
            return true;
        }
        return isVisibleForElementValueConditions(section.elementValueBasedVisibilityConditions, elementValues);
    });

    return visible;
};

export const getVisibleSessionIdsForUser = (slotData: SlotData, moduleModifiers: string[], userProperties: string[]): `session_${string}`[] => {
    return getOrderedContentIds(Object.keys(slotData.sessions) as (`session_${string}`)[]).filter(
        (sessionId) =>
        sessionIsVisibleForUserWithModifiers(userProperties, slotData.sessions[sessionId], moduleModifiers)
    );
};

export const moduleIsVisibleForUser = (module: ModuleDataDB, moduleModifiers: string[], allModules: Array<ModuleDataDB>, userStartDateISO: string | undefined, userProperties: string[]): boolean => {
    const moduleDisplayRulePasses = !module.generalDisplayRule || isVisibleForUserProperties(module.generalDisplayRule, userProperties);
    const slotHasVisibleSections = _.some(module.slots, slot => slotIsVisibleForUser(userProperties, slot, moduleModifiers));
    const timeBasedRulesPass = isVisibleForTimeBasedRules(module, allModules, userStartDateISO);

    return moduleDisplayRulePasses && slotHasVisibleSections && timeBasedRulesPass;
};

export const getModuleAvailableDate = (
    module: ModuleDataDB,
    allModules: ModuleDataDB[],
    moduleModifiers: string[],
    userStartDateISO: string | undefined,
    userProperties: string[],
    now: Date = new Date()
): Date | null => {
    // If it doesn't pass module-level display rule, it's never available
    if (module.generalDisplayRule && !isVisibleForUserProperties(module.generalDisplayRule, userProperties)) {
        return null;
    }

    // If it doesn't pass display rules, it's never available
    const passesDisplayRules =
        _.some(module.slots, slot =>
        _.some(Object.values(slot.sessions), session =>
            _.some(session.sections, section => {
            const userVisible = isVisibleForUserProperties(section.generalDisplayRule, userProperties);
            const modifierVisible = section.moduleDisplayRule
                ? isVisibleForModuleModifiers(section.moduleDisplayRule, moduleModifiers)
                : true;
            return userVisible && modifierVisible;
            })
        )
        );

    if (!passesDisplayRules) {
        return null;
    }

    // No time-based visibility => available now
    if (!userStartDateISO || !module.userSurveyStartBasedVisibility?.duration) {
        return now;
    }

    // Use the common getResolvedModuleStartDate which handles scheduling
    const moduleStartDate = getResolvedModuleStartDate(module, allModules, userStartDateISO);
    return moduleStartDate;
};

export const sessionHasRecommendedSections = (session: SessionData, recommendationProperties: StateProperty[], userProperties: string[]): boolean => {
    return _.some(session.sections, sec => {
        if (!isVisibleForUserProperties(sec.generalDisplayRule, userProperties)) {
            return false;
        }
        if (!sec.recommendationRule) {
            return true;
        }
        return applyVisibilityRule(sec.recommendationRule, recommendationProperties);
    });
};

export const sessionIsFinished = (
    slotId: `slot_${string}`,
    sessionId: `session_${string}`,
    properties: StateProperty[],
    finished: FinishedState,
    checkProperties: boolean
): boolean => {
    const finishedProps = finished[`${slotId}__${sessionId}`];
    if (finishedProps === undefined) {
        return false;
    }

    if (checkProperties) {
        return properties.every(prop => finishedProps.includes(prop));
    }

    return true;
};

export const slotIsFinished = (slotId: `slot_${string}`, visibleSessionIds: `session_${string}`[], finished: FinishedState, checkProperties: boolean): boolean => {
    return visibleSessionIds.every(sessionId => sessionIsFinished(slotId, sessionId, ['PREGNANCY'], finished, checkProperties) || sessionIsFinished(slotId, sessionId, ['POSTPARTUM'], finished, checkProperties));
};

export const getSkipToUrlForSingleItems = (moduleData: ModuleDataDB, userProperties: string[], moduleState?: ModuleStateDB): string | false => {
    if (moduleData.navigateToSlotOrSessionIfObvious === undefined || moduleData.navigateToSlotOrSessionIfObvious === false) {
        return false;
    }
    const moduleModifiers = moduleState?.modifiers || [];
    const slots: { [id: string]: SlotData } = moduleData.slots;

    if (!slots) {
        return false;
    }

    const slotIds = Object.keys(slots);
    if (slotIds.length !== 1) {
        return false;
    }

    const slotId = slotIds[0];
    const slot = slots[slotId] as SlotData;

    if (!slot.sessions || typeof slot.sessions !== 'object') {
        return `${slotId}`;
    }

    const visibleSessionIds = getVisibleSessionIdsForUser(slot, moduleModifiers, userProperties);
    if (visibleSessionIds.length === 1) {
        const firstSession = slot.sessions[visibleSessionIds[0]];
        const elementValues = moduleState?.elementValues || {};
        const visibleSections = getVisibleSections(firstSession, moduleModifiers, elementValues, userProperties);
        const firstSectionId = visibleSections[0]?.id;
        if (!firstSectionId) {
            return `${slotId}`;
        }
        return `${slotId}/${visibleSessionIds[0]}/${firstSectionId}`;
    }

    return `${slotId}`;
};

export const getSkipToUrlForAutoSkip = (moduleData: ModuleDataDB, userProperties: string[], moduleState?: ModuleStateDB): string | false => {
    const moduleModifiers = moduleState?.modifiers || [];
    const slots: { [id: string]: SlotData } = moduleData.slots;

    if (!slots) {
        return false;
    }

    const slotIds = getOrderedContentIds(Object.keys(slots));

    const slotId = slotIds[0];
    const slot = slots[slotId] as SlotData;

    if (!slot.sessions || typeof slot.sessions !== 'object') {
        return `${slotId}`;
    }

    const visibleSessionIds = getVisibleSessionIdsForUser(slot, moduleModifiers, userProperties);
    if (visibleSessionIds.length > 0) {
        const firstSession = slot.sessions[visibleSessionIds[0]];
        const elementValues = moduleState?.elementValues || {};
        const visibleSections = getVisibleSections(firstSession, moduleModifiers, elementValues, userProperties);
        const firstSectionId = visibleSections[0]?.id;
        if (!firstSectionId) {
            return `${slotId}`;
        }
        return `${slotId}/${visibleSessionIds[0]}/${firstSectionId}`;
    }

    return `${slotId}`;
};

export const getNextVisibleSessionUrl = (
    moduleData: ModuleDataDB,
    currentSlotId: `slot_${string}`,
    currentSessionId: `session_${string}`,
    userProperties: string[],
    moduleState?: ModuleStateDB
): string | false => {
    const slots: { [id: string]: SlotData } = moduleData.slots;
    if (!slots) {
        return false;
    }

    const slotIds = getOrderedContentIds(Object.keys(slots) as (`slot_${string}`)[]);
    const currentSlotIndex = slotIds.indexOf(currentSlotId);
    if (currentSlotIndex === -1) {
        return false;
    }

    const moduleModifiers = moduleState?.modifiers || [];
    const elementValues = moduleState?.elementValues || {};

    for (let slotIndex = currentSlotIndex; slotIndex < slotIds.length; slotIndex++) {
        const slotId = slotIds[slotIndex];
        const slot = slots[slotId];
        if (!slot?.sessions || typeof slot.sessions !== 'object') {
            continue;
        }
        if (!slotIsVisibleForUser(userProperties, slot, moduleModifiers)) {
            continue;
        }

        const sessionIds = getOrderedContentIds(Object.keys(slot.sessions) as (`session_${string}`)[]);
        const firstCandidateIndex = slotId === currentSlotId
            ? sessionIds.indexOf(currentSessionId) + 1
            : 0;

        // A missing current session must not restart the current slot from its first session.
        if (slotId === currentSlotId && firstCandidateIndex === 0) {
            continue;
        }

        const visibleSessionIds = getVisibleSessionIdsForUser(slot, moduleModifiers, userProperties);
        for (const sessionId of sessionIds.slice(firstCandidateIndex)) {
            if (!visibleSessionIds.includes(sessionId)) {
                continue;
            }

            const visibleSections = getVisibleSections(
                slot.sessions[sessionId],
                moduleModifiers,
                elementValues,
                userProperties
            );
            const firstSectionId = visibleSections[0]?.id;
            if (firstSectionId) {
                return `${slotId}/${sessionId}/${firstSectionId}`;
            }
        }
    }

    return false;
};

export const getSkipToUrlForStartSectionSelection = (moduleData: ModuleDataDB, userProperties: string[], moduleState?: ModuleStateDB): string | false => {
    if (!moduleData.startSection) {
        return false;
    }

    const moduleModifiers = moduleState?.modifiers || [];
    const elementValues = moduleState?.elementValues || {};
    const slots: { [id: string]: SlotData } = moduleData.slots;

    if (!slots) {
        return false;
    }

    for (const slotId of getOrderedContentIds(Object.keys(slots))) {
        const slot = slots[slotId] as SlotData;

        if (!slot.sessions || typeof slot.sessions !== 'object') {
            continue;
        }

        if (!slotIsVisibleForUser(userProperties, slot, moduleModifiers)) {
            continue;
        }

        const visibleSessionIds = getVisibleSessionIdsForUser(slot, moduleModifiers, userProperties);
        for (const sessionId of visibleSessionIds) {
            const session = slot.sessions[sessionId];
            const visibleSections = getVisibleSections(session, moduleModifiers, elementValues, userProperties);
            const firstSection = visibleSections.find(section =>
                section.elementValueBasedVisibilityConditions?.some(condition =>
                    condition.sourceSectionId === moduleData.startSection
                )
            );

            if (firstSection) {
                return `${slotId}/${sessionId}/${firstSection.id}`;
            }
        }
    }

    return false;
};